import { Route, Routes, Outlet } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../../../../_metronic/layout/core'
import React, { FC, Suspense, useState, useEffect, useMemo, useCallback } from 'react'
import { WithChildren } from '../../../../../../_metronic/helpers'
import { KTCard } from '../../../../../../_metronic/helpers'
import { Card4 } from '../../../../../../_metronic/partials/content/cards/Card4'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { ProductionWrapper } from './ProductionManagementWrapper'
import { PrintTagWrapper } from './PrintTagWrapper'
import { useNavigate } from 'react-router-dom'
import { CustomBreadcrumb } from './CBC'

// Types for the API response
interface MaterialProcess {
    id: string
    name: string
    icon?: string
    processType: string
    processCost: number
    status: string
}

interface ApiResponse {
    data: MaterialProcess[]
    totalElements: number
    totalPages: number
}

interface AuthData {
    data: {
        token: string
        franchiseeid: string
        pickuppointid: string
    }
}

const usersBreadcrumbs: Array<PageLink> = [
    {
        title: 'Working Stock',
        path: '/apps/transactions/working-stock',
        isSeparator: false,
        isActive: false,
    },
    {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
    },
]

// Custom hook for API calls - optimized
const useMaterialProcesses = () => {
    const [processes, setProcesses] = useState<MaterialProcess[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const fetchMaterialProcesses = useCallback(async (page = 0, size = 10) => {
        setLoading(true)
        setError(null)

        try {
            const authData = localStorage.getItem('kt-auth-react-v')
            if (!authData) {
                throw new Error('No authentication token found')
            }

            const parsedAuthData: AuthData = JSON.parse(authData)
            const { token, franchiseeid, pickuppointid } = parsedAuthData.data

            const response = await fetch(
                `${process.env.REACT_APP_BASE_API_URL}materialProcesses?page=${page}&size=${size}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'franchiseeid': franchiseeid,
                        'pickuppointid': pickuppointid,
                    },
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data: ApiResponse = await response.json()
            setProcesses(data.data || [])
            return data

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred'
            setError(errorMessage)
            console.error('Error fetching material processes:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    return { processes, loading, error, fetchMaterialProcesses }
}

// Memoized WorkingStockMenu component
interface WorkingStockMenuProps {
    processes: MaterialProcess[]
}

const WorkingStockMenu: FC<WorkingStockMenuProps> = React.memo(({ processes }) => {
    const createProcessPath = useCallback((processName: string, processId: string) => {
        return `${processName.toLowerCase().replace(/\s+/g, '-')}-${processId.slice(-6)}`
    }, [])

    const workingStockFeatures = useMemo(() => {
        return processes.map((process) => ({
            name: process.name,
            icon: process.icon || '/media/svg/files/default.png',
            to: createProcessPath(process.name, process.id),
            processType: process.processType,
            status: process.status,
            processCost: process.processCost,
            isDynamic: true
        }))
    }, [processes, createProcessPath])

    return (
        <KTCard addCard={false}>
            <div className='row g-xl-4'>
                {workingStockFeatures.map((feature, idx) => (
                    <div key={`${feature.name}-${idx}`} className='col col-lg-2'>
                        <Link className={clsx('menu-link without-sub')} to={feature.to}>
                            <Card4
                                icon={feature.icon}
                                title={feature.name}
                                description=""
                            />
                        </Link>
                    </div>
                ))}
            </div>
        </KTCard>
    )
})

WorkingStockMenu.displayName = 'WorkingStockMenu'

const WorkingStockPage = () => {
    const { processes, loading, error, fetchMaterialProcesses } = useMaterialProcesses()

    useEffect(() => {
        fetchMaterialProcesses()
    }, [fetchMaterialProcesses])

    const createProcessPath = useCallback((processName: string, processId: string) => {
        return `${processName.toLowerCase().replace(/\s+/g, '-')}-${processId.slice(-6)}`
    }, [])

    const routes = useMemo(() => {
        const processRoutes = processes.map((process) => {
            const processPath = createProcessPath(process.name, process.id)
            return (
                <Route
                    key={process.id}
                    path={processPath}
                    element={
                        <SuspensedView>
                            <PageTitle breadcrumbs={usersBreadcrumbs}>
                                {process.name}
                            </PageTitle>
                            <ProductionWrapper
                                processName={process.name}
                                processId={process.id}
                            />
                        </SuspensedView>
                    }
                />
            )
        })

        const printTagRoutes = processes.map((process) => {
            const processPath = createProcessPath(process.name, process.id)
            return (
                <Route
                    key={`${process.id}-print-tag`}
                    path={`${processPath}/print-tag/:productionId`}
                    element={
                        <SuspensedView>
                            <div style={{marginBottom:"20px"}}>
                                <p className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                                    Print Tags
                                </p>
                                <CustomBreadcrumb items={[
                                    { title: 'Working Stock', path: '/apps/transactions/working-stock' },
                                    { title: process.name, path: `/apps/transactions/working-stock/${processPath}` },
                                    { title: 'Print Tags', isActive: true }
                                ]} />
                            </div>
                            <PrintTagWrapper
                                processName={process.name}
                                processId={process.id}
                            />
                        </SuspensedView>
                    }
                />
            )
        })

        return [...processRoutes, ...printTagRoutes]
    }, [processes, createProcessPath])


    return (
        <Routes>
            <Route element={<Outlet />}>
                <Route index element={<WorkingStockMenu processes={processes} />} />
                {routes}
            </Route>
        </Routes>
    )
}

const SuspensedView: FC<WithChildren> = React.memo(({ children }) => {
    return <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
})

SuspensedView.displayName = 'SuspensedView'

export default WorkingStockPage